import React, { memo, useCallback, useMemo } from 'react';

import {
  arrayOf, bool, func, shape, string,
} from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { ShopifyColorful } from '@picsio/icons';

import DetailsPlaceholder from '../../../DetailsBlock/Placeholder';
import Logger from '../../../../services/Logger';
import l10n from '../../../../shared/strings';
import { navigate } from '../../../../helpers/history';
import {
  detachAssetsFromTargets,
  detachProductVariantFromAssets,
  removeAssets,
} from '../../../../store/products/actions';
import { getListingChannelId } from '../../../../store/products/helpers/channelIdHelpers';
import { resolveChannelLabel } from '../../../../store/products/helpers/showPublishedToChannelToast';
import { SHOPIFY_CHANNEL_TYPE } from '../../../teammates/views/Channels/helpers/salesChannelState';
import ProductLinksSection from './ProductLinksSection';
import { groupProductLinks, UNKNOWN_CHANNEL_ID } from './helpers/groupProductLinks';
import {
  buildListingProductDetachTargets,
  getAffectedAssetIds,
  getAffectedAssetIdsForProduct,
} from './helpers/productLinkDetach';
import {
  isProductLinkActionable,
  isVariantLinkActionable,
} from './helpers/productLinkActionable';
import {
  isProductLinkDetachable,
  isVariantLinkDetachable,
} from './helpers/productLinkDetachable';
import {
  resolveProductNavigation,
  resolveVariantNavigation,
} from './helpers/resolveProductLinkNavigation';
import s from './styles.module.scss';

const ProductLinksSections = ({
  products,
  productVariants,
  selectedAssets,
  selectedAssetItems,
  disabled,
  changeTree,
  openedTree,
}) => {
  const dispatch = useDispatch();
  const masterProducts = useSelector((state) => state.products.items);
  const teamSalesChannels = useSelector((state) => state.user?.team?.salesChannels || []);

  const grouped = useMemo(
    () => groupProductLinks({
      products, productVariants, masterProducts, salesChannels: teamSalesChannels,
    }),
    [products, productVariants, masterProducts, teamSalesChannels],
  );

  const strings = l10n.DETAILS;

  const placeholder = useMemo(
    () => (
      <DetailsPlaceholder>
        {strings.placeholderProductLinks(selectedAssets.length)}
      </DetailsPlaceholder>
    ),
    [selectedAssets.length, strings],
  );

  const getChannelIcon = useCallback((channelId) => {
    if (channelId === UNKNOWN_CHANNEL_ID) return null;

    const normalizedChannelId = String(channelId);
    const salesChannel = teamSalesChannels.find(
      (channel) => String(channel._id) === normalizedChannelId,
    )
      || teamSalesChannels.find((channel) => channel.name === normalizedChannelId)
      || teamSalesChannels.find((channel) => channel.type === normalizedChannelId);

    if (salesChannel?.type === SHOPIFY_CHANNEL_TYPE) {
      return <ShopifyColorful />;
    }

    return null;
  }, [teamSalesChannels]);

  const getChannelTitle = useCallback((channelId) => {
    if (channelId === UNKNOWN_CHANNEL_ID) {
      return strings.textProductLinksUnknownChannel;
    }

    const label = resolveChannelLabel(channelId, teamSalesChannels);
    if (label) return label;
    return channelId.charAt(0).toUpperCase() + channelId.slice(1);
  }, [strings.textProductLinksUnknownChannel, teamSalesChannels]);

  const handleNavigateProduct = useCallback((productLink) => {
    const navigation = resolveProductNavigation(productLink);
    if (!navigation?.masterProductId) return;

    Logger.log('User', 'InfoPanelProductClick', {
      masterProductId: navigation.masterProductId,
      productId: productLink._id,
    });

    navigate(`/product/${navigation.masterProductId}`);

    if (changeTree && openedTree !== 'products') {
      changeTree('products', true);
    }
  }, [changeTree, openedTree]);

  const handleNavigateVariant = useCallback((productVariant) => {
    const navigation = resolveVariantNavigation(productVariant);
    if (!navigation) return;

    Logger.log('User', 'InfoPanelProductVariantClick', {
      masterProductId: navigation.masterProductId,
      masterVariantId: navigation.masterVariantId,
      variantType: productVariant.type,
    });

    navigate(`/product/${navigation.masterProductId}/${navigation.masterVariantId}`);

    if (changeTree && openedTree !== 'products') {
      changeTree('products', true);
    }
  }, [changeTree, openedTree]);

  const handleDetachVariant = useCallback((productVariant) => {
    if (!isVariantLinkDetachable(productVariant)) return;

    const assetIds = getAffectedAssetIds(selectedAssetItems, productVariant);
    if (!assetIds.length) return;

    const navigation = resolveVariantNavigation(productVariant);
    if (!navigation?.masterProductId) return;

    Logger.log('User', 'InfoPanelProductVariantDetach', {
      variantId: productVariant.variantId,
      variantType: productVariant.type,
      assetCount: assetIds.length,
      assetId: assetIds.join(', '),
    });

    dispatch(detachProductVariantFromAssets({
      assetIds,
      productVariant,
      masterProductId: navigation.masterProductId,
    }));
  }, [dispatch, selectedAssetItems]);

  const handleDetachProduct = useCallback((productLink, { isMaster }) => {
    if (!isProductLinkDetachable(productLink, { isMaster })) return;

    const assetIds = getAffectedAssetIdsForProduct(
      selectedAssetItems,
      productLink,
      { isMaster },
    );
    if (!assetIds.length) return;

    if (isMaster) {
      const masterProduct = masterProducts.find(
        ({ _id }) => String(_id) === String(productLink._id),
      ) || productLink;

      Logger.log('User', 'InfoPanelRemoveFromProduct', {
        productId: productLink._id,
        assetCount: assetIds.length,
      });

      dispatch(removeAssets({ assetIds, product: masterProduct }));
      return;
    }

    const { masterProductId } = productLink;
    const channelId = getListingChannelId(productLink);
    if (!masterProductId || !channelId) return;

    const targets = buildListingProductDetachTargets({
      listingId: productLink._id,
      channelId,
      assetItems: selectedAssetItems.filter((asset) => assetIds.includes(asset._id)),
      isDraft: productLink.isDraft === true,
    });

    Logger.log('User', 'InfoPanelRemoveFromListingProduct', {
      listingId: productLink._id,
      channelId,
      assetCount: assetIds.length,
    });

    dispatch(detachAssetsFromTargets({
      assetIds,
      masterProductId,
      targets,
    }));
  }, [dispatch, masterProducts, selectedAssetItems]);

  if (!grouped.hasLinks) {
    return <div className={s.root}>{placeholder}</div>;
  }

  return (
    <div className={s.root} data-qa="product-links-sections">
      <ProductLinksSection
        title={strings.textProductLinksMaster}
        products={grouped.master.products}
        variants={grouped.master.variants}
        isMaster
        disabled={disabled}
        tooltipTextClose={strings.tooltipProductLinkDetach}
        tooltipTextCloseReadOnly={strings.tooltipPublishedLayerReadOnly}
        isProductActionable={(product) => isProductLinkActionable(product, { isMaster: true })}
        isProductDetachable={(product) => isProductLinkDetachable(product, { isMaster: true })}
        isVariantActionable={isVariantLinkActionable}
        isVariantDetachable={isVariantLinkDetachable}
        onNavigateProduct={handleNavigateProduct}
        onNavigateVariant={handleNavigateVariant}
        onDetachProduct={handleDetachProduct}
        onDetachVariant={handleDetachVariant}
      />
      {grouped.channels.map((channel) => (
        <ProductLinksSection
          key={channel.channelId}
          title={getChannelTitle(channel.channelId)}
          titleIcon={getChannelIcon(channel.channelId)}
          products={channel.products}
          variants={channel.variants}
          disabled={disabled}
          tooltipTextClose={strings.tooltipProductLinkDetach}
          tooltipTextCloseReadOnly={strings.tooltipPublishedLayerReadOnly}
          isProductActionable={(product) => isProductLinkActionable(product, { isMaster: false })}
          isProductDetachable={(product) => isProductLinkDetachable(product, { isMaster: false })}
          isVariantActionable={isVariantLinkActionable}
          isVariantDetachable={isVariantLinkDetachable}
          onNavigateProduct={handleNavigateProduct}
          onNavigateVariant={handleNavigateVariant}
          onDetachProduct={handleDetachProduct}
          onDetachVariant={handleDetachVariant}
        />
      ))}
    </div>
  );
};

ProductLinksSections.propTypes = {
  products: arrayOf(shape({
    _id: string,
    title: string,
    isDraft: bool,
  })),
  productVariants: arrayOf(shape({
    variantId: string,
    productId: string,
    title: string,
    type: string,
    isDraft: bool,
  })),
  selectedAssets: arrayOf(string).isRequired,
  selectedAssetItems: arrayOf(shape({})).isRequired,
  disabled: bool,
  changeTree: func,
  openedTree: string,
};

ProductLinksSections.defaultProps = {
  products: [],
  productVariants: [],
  disabled: false,
  changeTree: null,
  openedTree: '',
};

export default memo(ProductLinksSections);
